import { useIntl } from 'react-intl';
import { z } from 'zod';
import Spacings from '~/modules/ui-kit/spacings';
import { useAppForm } from '~/components/Form';
import formMessages from '~/components/form-messages';
import messages from './messages';
import styles from './styles.module.css';

type EventFormValues = {
  eventDate: string | null;
  eventTime: string | null;
  location: string;
  description: string;
};

type EventFormProps = {
  onSubmit: (values: EventFormValues) => void;
  initialValues?: Partial<EventFormValues>;
};

export const EventForm = ({ onSubmit, initialValues }: EventFormProps) => {
  const intl = useIntl();

  const schema = z.object({
    eventDate: z.string({
      error: intl.formatMessage(formMessages.required),
    }),
    eventTime: z.string({
      error: intl.formatMessage(formMessages.required),
    }),
    location: z.string({
      error: intl.formatMessage(formMessages.required),
    }),
    description: z.string({
      error: intl.formatMessage(formMessages.required),
    }),
  });

  const form = useAppForm({
    defaultValues: {
      eventDate: null,
      eventTime: null,
      location: '',
      description: '',
      ...initialValues,
    } as EventFormValues,
    validators: {
      onChange: schema,
    },
    onSubmit: ({ value }) => onSubmit(value),
  });

  return (
    <form
      id="event-form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className={styles.form}
    >
      <Spacings.Stack scale="m">
        <Spacings.Inline>
          <div>
            <form.AppField name="eventDate">
              {(field) => (
                <field.DateField
                  label={intl.formatMessage(messages.eventDateLabel)}
                />
              )}
            </form.AppField>
          </div>
          <div>
            <form.AppField name="eventTime">
              {(field) => (
                <field.TimeField
                  label={intl.formatMessage(messages.eventTimeLabel)}
                />
              )}
            </form.AppField>
          </div>
        </Spacings.Inline>
        <div>
          <form.AppField name="location">
            {(field) => (
              <field.TextField
                label={intl.formatMessage(messages.locationLabel)}
                fullWidth
              />
            )}
          </form.AppField>
        </div>
        <div>
          <form.AppField name="description">
            {(field) => (
              <field.TextField
                label={intl.formatMessage(messages.descriptionLabel)}
                multiline
                rows={3}
                fullWidth
              />
            )}
          </form.AppField>
        </div>
      </Spacings.Stack>
    </form>
  );
};

export default EventForm;
